import { useMemo } from 'react';
import { useCalculatePrices } from './use-calculate-prices';

/**
 * handle use interface ProductInterface
 */
// eslint-disable-next-line
type ProductInterface = any;

export interface UseFinalPriceValidationResult {
    isValid: boolean;
    shouldShowContact: boolean;
    priceData: {
        maximumPrice: ReturnType<typeof useCalculatePrices>['maximumPrice'];
        minimumPrice: ReturnType<typeof useCalculatePrices>['minimumPrice'];
        minPrice: string;
        maxPrice: string;
        discount: string | null;
        basePrice: string | null;
        price: string;
    } | null;
}

/**
 * Custom hook to validate and calculate price data for product display
 *
 * Validates:
 * 1. Product has required price_range structure
 * 2. Price data exists (not undefined/null)
 * 3. Distinguishes between "no data" and "price equals zero"
 *
 * @param product - Product interface with price information
 * @returns Object with validation status and price data
 */

export const useFinalPriceValidation = (product: ProductInterface): UseFinalPriceValidationResult => {
    // Call hooks unconditionally (React hooks rules)
    const priceCalculation = useCalculatePrices(product);

    return useMemo(() => {
        // Validation 1: Check if product has required price_range structure
        if (!product?.price_range?.minimum_price?.final_price) {
            return {
                isValid: false,
                shouldShowContact: true,
                priceData: null,
            };
        }

        const { maximumPrice, minimumPrice, minPrice, maxPrice, discount, basePrice, price } = priceCalculation;

        // Validation 2: Validate that price data exists (not just checking for zero values)
        // undefined/null means no data, 0 is a valid price
        if (
            maximumPrice?.value === undefined ||
            maximumPrice?.value === null ||
            minimumPrice?.value === undefined ||
            minimumPrice?.value === null ||
            !price
        ) {
            return {
                isValid: false,
                shouldShowContact: true,
                priceData: null,
            };
        }

        // All validations passed, return valid data
        return {
            isValid: true,
            shouldShowContact: false,
            priceData: {
                maximumPrice,
                minimumPrice,
                minPrice,
                maxPrice,
                discount,
                basePrice,
                price,
            },
        };
    }, [product, priceCalculation]);
};
